
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Pricing } from "@/components/landing/Pricing";
import { X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { useAuth } from "@/components/AuthProvider";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { useEffect } from "react";

interface RequiredSubscriptionDialogProps {
  open: boolean;
  onClose?: () => void;
  isUnpaid?: boolean;
}

export const RequiredSubscriptionDialog = ({
  open,
  onClose,
  isUnpaid = false
}: RequiredSubscriptionDialogProps) => {
  const isMobile = useIsMobile();
  const { t: dashboardT } = useTranslations("dashboard");
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { sendViewContentEvent, sendLeadEvent } = useMetaPixel();
  
  // Enviar eventos Meta Pixel quando o dialog é aberto
  useEffect(() => {
    if (open) {
      if (isUnpaid) {
        sendViewContentEvent('Payment Required Dialog', 'subscription_dialog');
        sendLeadEvent('Payment Required', 0);
      } else {
        sendViewContentEvent('Upgrade Required Dialog', 'subscription_dialog');
        sendLeadEvent('Upgrade Required', 0);
      }
    }
  }, [open, isUnpaid, sendViewContentEvent, sendLeadEvent]);
  
  const handleOpenChange = (open: boolean) => {
    if (!open && onClose) {
      onClose();
    }
  };

  // Check if user had a paid plan that expired
  const hadPaidPlanThatExpired = () => {
    return (profile?.subscription_plan === 'pro' || profile?.subscription_plan === 'premium') && 
           (profile?.subscription_status === 'canceled' || profile?.subscription_status === 'past_due');
  };

  // Get the appropriate message
  const getDialogMessage = () => {
    if (isUnpaid) {
      return dashboardT("subscription.paymentRequiredMessage");
    }
    
    if (hadPaidPlanThatExpired()) {
      return dashboardT("subscription.renewSubscription");
    }
    
    return dashboardT("subscription.upgradeAfterTrial");
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`${isMobile ? "max-w-[95%] p-4" : "max-w-5xl"} h-[65vh] mt-8 flex flex-col`}>
        <DialogHeader className="relative">
          <DialogTitle className="text-2xl font-bold text-center px-0 py-[18px]">
            {isUnpaid 
              ? dashboardT("subscription.paymentRequiredTitle") 
              : dashboardT("subscription.trialUpgradeTitle")
            }
          </DialogTitle>
          <DialogClose className="absolute right-0 top-0 rounded-sm opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 p-2 focus:outline-none">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </DialogHeader>
        
        <ScrollArea className="flex-1 overflow-y-auto pr-4">
          <div className="py-6">
            <p className="text-gray-600 dark:text-gray-300 text-center mb-8">
              {getDialogMessage()}
            </p>
            <Pricing hideFreePlan expiredTrial />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
